"use client";

import { useQuery } from "@tanstack/react-query";
import { QUERIES } from "modules/common/query";
import { API_ROUTES } from "utils/api-route.constants";
import { STATIC_DATA_CONFIG } from "modules/common/react-query-configs";
import type { UserPreview } from "modules/users/model/userPreview";

/**
 * Lazily fetches the complete hover-card payload in one public, edge-cacheable
 * request. `enabled` only flips true after the hover-intent timer, so sweeping
 * across a grid still fetches nothing.
 */
export function useUserPreview(userId: string, enabled: boolean) {
  const preview = useQuery({
    queryKey: [QUERIES.USER_PREVIEW, userId],
    queryFn: async () => {
      // This endpoint exposes only the target's public profile view. Omit all
      // credentials so signed-in hover requests can use the shared edge cache.
      const response = await fetch(API_ROUTES.USERS.PREVIEW(userId), {
        credentials: "omit",
      });
      if (!response.ok) {
        throw new Error(`Failed to load user preview (${response.status})`);
      }
      return (await response.json()) as UserPreview;
    },
    enabled: enabled && !!userId,
    ...STATIC_DATA_CONFIG,
  });

  return {
    user: preview.data?.user,
    haves: preview.data?.haves,
    wants: preview.data?.wants,
    isLoading: preview.isLoading,
  };
}
